import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API, Designer, DesignerAPI } from '@brg/api';
import { BrgButton, ButtonVariant, useToasts } from '@brg/ui';
import { useModal } from 'core';
import { TextInputField } from 'shared/pages/CourseEdit/components/TextInputField';
import { SelectField } from 'shared/pages/CourseEdit/components/SelectField';
import { DatePickerField } from 'shared/pages/CourseEdit/components/DatePickerField';
import CheckLongreadRadioButton from 'shared/pages/teams/components/CheckLongreadRadioButton';
import { useGetModulesList } from 'shared/hooks/api/LMS/useGetModulesList';
import { useGetGroupsInfoByCourseId } from 'shared/hooks/api/LMS/useGetGroupsInfoByCourseId';
import { useGetLMSModule } from 'shared/hooks/api/useGetLMSModule';
import { teachingEvaluationApi } from 'shared/pages/CourseEdit/teachingEvaluation/api';
import { resolveStatusFromDates } from 'shared/pages/CourseEdit/teachingEvaluation/mockStore';
import {
    TeachingEvaluationFormat,
    TeachingEvaluationVotingMode,
} from 'shared/pages/CourseEdit/teachingEvaluation/types';
import { toDisplayString } from 'shared/pages/CourseEdit/teachingEvaluation/toDisplayString';
import ProjectAuthorsEdit from 'shared/pages/projectEdit/ProjectAuthorsEdit';
import { ProjectAuthorsWrapper } from 'shared/pages/projectEdit/styles';
import { AuthorT } from 'shared/pages/projectEdit/types';
import {
    BackButton,
    ChipList,
    ChipRemove,
    FooterActions,
    FooterBar,
    FooterCancel,
    ModalInner,
    ModalRoot,
    ModalTitle,
    RadioList,
    SectionBlock,
    SectionHeading,
    TeacherChip,
    VotingBlock,
    VotingEmptyHint,
    VotingLabel,
} from './styles';

interface SelectOption {
    label: string;
    value: string;
}

const toStartOfLocalDayIso = (date: Date): string => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next.toISOString();
};

const toEndOfLocalDayIso = (date: Date): string => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next.toISOString();
};

const designerKey = (designer: Pick<Designer<'Local'>, 'designerId' | 'accountId'>): string =>
    String(designer.designerId || designer.accountId || '');

const designerDisplayName = (designer: Designer<'Local'>, lang: string): string =>
    [toDisplayString(designer.name, lang), toDisplayString(designer.surName, lang), toDisplayString(designer.middleName, lang)]
        .filter(Boolean)
        .join(' ')
        .trim() || designerKey(designer);

const stubDesigner = (id: string, name: string): AuthorT =>
    ({
        designerId: id,
        accountId: id,
        name,
        surName: '',
        middleName: '',
        nameCache: name,
        about: '',
        socialNetworks: [],
        workPlaces: [],
        education: [],
    }) as AuthorT;

const moduleTeacherToLocal = (p: any, lang: string): AuthorT => {
    const name = toDisplayString(p?.name, lang) || toDisplayString(p?.firstName, lang);
    const surName = toDisplayString(p?.surName, lang) || toDisplayString(p?.lastName, lang);
    const middleName = toDisplayString(p?.middleName, lang);
    const fullName = toDisplayString(p?.fullName, lang) || toDisplayString(p?.nameCache, lang);
    const id = String(p?.designerId || p?.accountId || p?.id || '');
    return {
        ...p,
        designerId: String(p?.designerId || id),
        accountId: String(p?.accountId || id),
        name: name || fullName,
        surName,
        middleName,
        nameCache: fullName || [name, surName, middleName].filter(Boolean).join(' '),
        about: toDisplayString(p?.about, lang),
        socialNetworks: Array.isArray(p?.socialNetworks) ? p.socialNetworks : [],
        workPlaces: Array.isArray(p?.workPlaces) ? p.workPlaces : [],
        education: Array.isArray(p?.education) ? p.education : [],
    } as AuthorT;
};

const mergeTeachersById = (list: AuthorT[]): AuthorT[] => {
    const unique = new Map<string, AuthorT>();
    list.forEach((teacher) => {
        const key = designerKey(teacher);
        if (key && !unique.has(key)) unique.set(key, teacher);
    });
    return Array.from(unique.values());
};

const designersFromGetList = (raw: unknown, lang: string): AuthorT[] => {
    const list = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { data?: unknown })?.data)
          ? ((raw as { data: unknown[] }).data)
          : [];
    return (API.toLang(list, lang) || []) as AuthorT[];
};

interface TeachingEvaluationCampaignModalProps {
    teamId: string;
    campaignId?: string;
    onSaved?: () => void;
}

const SOP_MODULE_STATUSES = [11];

const FORMAT_OPTIONS: TeachingEvaluationFormat[] = ['module', 'teacher', 'moduleAndTeacher'];

const getVotingModes = (format: TeachingEvaluationFormat): TeachingEvaluationVotingMode[] => {
    if (format === 'module') return ['general', 'custom'];
    return ['general', 'byGroups', 'custom'];
};

const getVotingHintKey = (format: TeachingEvaluationFormat, mode: TeachingEvaluationVotingMode) => {
    if (format === 'module') {
        if (mode === 'custom') return 'moduleCustom';
        return 'moduleGeneral';
    }
    if (format === 'teacher') {
        if (mode === 'byGroups') return 'teacherByGroups';
        if (mode === 'custom') return 'teacherCustom';
        return 'teacherGeneral';
    }
    if (mode === 'byGroups') return 'moduleAndTeacherByGroups';
    if (mode === 'custom') return 'moduleAndTeacherCustom';
    return 'moduleAndTeacherGeneral';
};

export const TeachingEvaluationCampaignModal: FC<TeachingEvaluationCampaignModalProps> = ({
    teamId,
    campaignId,
    onSaved,
}) => {
    const { t, i18n } = useTranslation();
    const modal = useModal();
    const toasts = useToasts();
    const te = 'teams.adminPage.courseEditPage.teachingEvaluation';
    const { modules } = useGetModulesList(teamId, null, SOP_MODULE_STATUSES);

    const [name, setName] = useState('');
    const [moduleIds, setModuleIds] = useState<string[]>([]);
    const [format, setFormat] = useState<TeachingEvaluationFormat>('moduleAndTeacher');
    const [votingMode, setVotingMode] = useState<TeachingEvaluationVotingMode>('general');
    const [groupIds, setGroupIds] = useState<string[]>([]);
    const [teachersByGroup, setTeachersByGroup] = useState<Record<string, AuthorT[]>>({});
    const [openAt, setOpenAt] = useState<Date | null>(null);
    const [closeAt, setCloseAt] = useState<Date | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(!!campaignId);
    const [moduleTeachers, setModuleTeachers] = useState<AuthorT[]>([]);

    const selectedModuleId = moduleIds[0] || '';
    const { LMSModuleInfo, isLoading: isModuleLoading } = useGetLMSModule(
        selectedModuleId,
        undefined,
        !selectedModuleId,
        false,
    );

    const moduleOptions = useMemo<SelectOption[]>(() => {
        const list = Array.isArray(modules) ? modules : [];
        return list
            .map((m: any, idx: number) => {
                const label =
                    toDisplayString(m?.title, i18n.language) ||
                    toDisplayString(m?.name, i18n.language) ||
                    t('course_edit.module_default_name', { index: idx + 1 });
                return {
                    label,
                    value: String(m?.projectId || m?.moduleId || m?.id || ''),
                };
            })
            .filter((o) => o.value);
    }, [modules, t, i18n.language]);

    const formatOptions = useMemo(
        () =>
            FORMAT_OPTIONS.map((value) => ({
                label: t(`${te}.format.${value}`),
                value,
            })),
        [t],
    );

    const votingModes = useMemo(() => getVotingModes(format), [format]);
    const { groups: courseGroups, isLoading: groupsLoading } = useGetGroupsInfoByCourseId(
        teamId,
        i18n.language,
    );

    const groupOptions = useMemo<SelectOption[]>(
        () =>
            courseGroups
                .map((group) => ({
                    label:
                        toDisplayString(group.name, i18n.language) ||
                        toDisplayString(group.title, i18n.language) ||
                        String(group.id || ''),
                    value: String(group.id || ''),
                }))
                .filter((option) => option.value),
        [courseGroups, i18n.language],
    );
    const votingLocked = !groupsLoading && groupOptions.length === 0;

    useEffect(() => {
        if (!votingModes.includes(votingMode)) {
            setVotingMode(votingModes[0]);
        }
    }, [votingModes, votingMode]);

    useEffect(() => {
        if (votingLocked && votingMode !== 'general') {
            setVotingMode('general');
        }
    }, [votingLocked, votingMode]);

    useEffect(() => {
        if (!selectedModuleId || isModuleLoading) {
            setModuleTeachers([]);
            return;
        }
        if (!LMSModuleInfo) {
            setModuleTeachers([]);
            return;
        }

        const fromObjects = mergeTeachersById(
            (Array.isArray(LMSModuleInfo.teachers) ? LMSModuleInfo.teachers : []).map((p) =>
                moduleTeacherToLocal(p, i18n.language),
            ),
        );
        const knownIds = new Set(
            fromObjects.flatMap((teacher) => [teacher.designerId, teacher.accountId].filter(Boolean)),
        );
        const missingIds = (LMSModuleInfo.teacherIds || []).map(String).filter((id) => id && !knownIds.has(id));

        if (!missingIds.length) {
            setModuleTeachers(fromObjects);
            return;
        }

        let cancelled = false;
        DesignerAPI.GetList({ designerIds: missingIds, limit: missingIds.length })
            .then((r) => r.json())
            .then((raw) => {
                if (cancelled) return;
                const fetched = designersFromGetList(raw, i18n.language).map((p) =>
                    moduleTeacherToLocal(p, i18n.language),
                );
                setModuleTeachers(mergeTeachersById([...fromObjects, ...fetched]));
            })
            .catch(() => {
                if (!cancelled) setModuleTeachers(fromObjects);
            });

        return () => {
            cancelled = true;
        };
    }, [LMSModuleInfo, isModuleLoading, selectedModuleId, i18n.language]);

    useEffect(() => {
        if (!moduleTeachers.length) return;
        setTeachersByGroup((prev) => {
            let changed = false;
            const next: Record<string, AuthorT[]> = {};
            Object.keys(prev).forEach((groupId) => {
                next[groupId] = (prev[groupId] || []).map((sel) => {
                    const keys = [sel.designerId, sel.accountId].filter(Boolean);
                    const fromModule = moduleTeachers.find(
                        (teacher) =>
                            keys.includes(teacher.designerId) || keys.includes(teacher.accountId),
                    );
                    if (fromModule && fromModule !== sel) {
                        changed = true;
                        return fromModule;
                    }
                    return sel;
                });
            });
            return changed ? next : prev;
        });
    }, [moduleTeachers]);

    useEffect(() => {
        if (!campaignId) return;
        let cancelled = false;
        setIsLoading(true);
        teachingEvaluationApi
            .get(campaignId)
            .then(async (campaign) => {
                if (!campaign || cancelled) return;
                setName((campaign.name || '').slice(0, 100));
                setModuleIds(
                    campaign.moduleIds?.length
                        ? campaign.moduleIds
                        : campaign.moduleId
                          ? [campaign.moduleId]
                          : [],
                );
                setFormat(campaign.format);
                setVotingMode(campaign.votingMode);
                setGroupIds(campaign.groupIds || []);
                setOpenAt(campaign.openAt ? new Date(campaign.openAt) : null);
                setCloseAt(campaign.closeAt ? new Date(campaign.closeAt) : null);

                const custom = campaign.customVoteGroups || [];
                const ids = Array.from(
                    new Set(custom.flatMap((group) => group.teacherIds).filter(Boolean)),
                );
                if (!ids.length) {
                    setTeachersByGroup({});
                    return;
                }
                const nameById = new Map<string, string>();
                (campaign.teacherIds || []).forEach((id, idx) => {
                    const name = campaign.teacherNames?.[idx];
                    if (id && name) nameById.set(id, name);
                });
                const byId = new Map<string, AuthorT>();
                try {
                    const raw = await DesignerAPI.GetList({ designerIds: ids, limit: ids.length }).then((r) =>
                        r.json(),
                    );
                    if (cancelled) return;
                    designersFromGetList(raw, i18n.language).forEach((designer) => {
                        if (designer.designerId) byId.set(designer.designerId, designer);
                        if (designer.accountId) byId.set(designer.accountId, designer);
                    });
                } catch {
                }
                if (cancelled) return;
                const resolveTeacher = (id: string) =>
                    byId.get(id) || stubDesigner(id, nameById.get(id) || id);
                setTeachersByGroup(
                    custom.reduce<Record<string, AuthorT[]>>((acc, group) => {
                        acc[group.groupId] = group.teacherIds.filter(Boolean).map(resolveTeacher);
                        return acc;
                    }, {}),
                );
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [campaignId, i18n.language]);

    const needsGroups = votingMode === 'custom';
    const needsTeachers =
        votingMode === 'custom' && (format === 'teacher' || format === 'moduleAndTeacher');
    const hasName = Boolean(name.trim());
    const hasModule = moduleIds.length > 0;
    const hasDates = Boolean(openAt && closeAt);
    const datesOrderOk =
        !openAt ||
        !closeAt ||
        new Date(closeAt).setHours(0, 0, 0, 0) >= new Date(openAt).setHours(0, 0, 0, 0);
    const hasGroups = !needsGroups || groupIds.length > 0;
    const hasTeachers =
        !needsTeachers ||
        (groupIds.length > 0 &&
            groupIds.every((id) => (teachersByGroup[id] || []).length > 0));
    const canSaveDraft = hasName && hasModule && hasDates && datesOrderOk;
    const canPublish = canSaveDraft && hasGroups && hasTeachers;

    const selectedModuleOpts = useMemo(
        () =>
            moduleIds.map(
                (id) => moduleOptions.find((option) => option.value === id) || { label: id, value: id },
            ),
        [moduleIds, moduleOptions],
    );

    const selectedGroupOpts = useMemo(
        () =>
            groupIds.map(
                (id) => groupOptions.find((g) => g.value === id) || { label: id, value: id },
            ),
        [groupIds, groupOptions],
    );

    const close = useCallback(() => modal.hide(), [modal]);

    const setGroupTeachers = useCallback((groupId: string, authors: AuthorT[]) => {
        setTeachersByGroup((prev) => ({ ...prev, [groupId]: authors }));
    }, []);

    const removeGroup = useCallback((groupId: string) => {
        setGroupIds((prev) => prev.filter((id) => id !== groupId));
        setTeachersByGroup((prev) => {
            if (!(groupId in prev)) return prev;
            const next = { ...prev };
            delete next[groupId];
            return next;
        });
    }, []);

    const buildPayload = (asDraft: boolean) => {
        const openIso = openAt ? toStartOfLocalDayIso(openAt) : '';
        const closeIso = closeAt ? toEndOfLocalDayIso(closeAt) : '';
        let status: 'draft' | 'scheduled' | 'active' | 'completed' = 'draft';
        if (!asDraft) {
            status =
                openIso && closeIso
                    ? resolveStatusFromDates(openIso, closeIso, 'active')
                    : 'active';
        }
        const customVoteGroups = needsGroups
            ? groupIds.map((groupId) => ({
                  groupId,
                  teacherIds: needsTeachers
                      ? (teachersByGroup[groupId] || []).map(designerKey).filter(Boolean)
                      : [],
              }))
            : [];
        const allTeachers = needsTeachers
            ? mergeTeachersById(groupIds.flatMap((groupId) => teachersByGroup[groupId] || []))
            : [];
        return {
            teamId,
            name: name.trim(),
            moduleId: selectedModuleOpts[0]?.value || '',
            moduleName: selectedModuleOpts[0]?.label || '',
            moduleIds: selectedModuleOpts.map((option) => option.value),
            format,
            votingMode,
            groupIds: needsGroups ? groupIds : [],
            customVoteGroups,
            teacherIds: allTeachers.map(designerKey).filter(Boolean),
            teacherNames: allTeachers.map((item) => designerDisplayName(item, i18n.language)),
            openAt: openIso,
            closeAt: closeIso,
            status,
        };
    };

    const handleSave = async (asDraft: boolean) => {
        if (asDraft) {
            if (!hasModule) {
                toasts.info(t(`${te}.modal.selectModule`));
                return;
            }
            if (!canSaveDraft) {
                toasts.info(t(`${te}.modal.fillRequired`));
                return;
            }
        } else {
            if (!datesOrderOk) {
                toasts.info(t(`${te}.modal.datesOrder`));
                return;
            }
            if (!canPublish) {
                toasts.info(t(`${te}.modal.fillRequired`));
                return;
            }
        }
        setIsSaving(true);
        try {
            const payload = buildPayload(asDraft);
            if (campaignId) {
                await teachingEvaluationApi.update(campaignId, payload);
            } else {
                await teachingEvaluationApi.create(payload);
            }
            toasts.info(t(asDraft ? `${te}.saved` : `${te}.published`));
            onSaved?.();
            close();
        } catch {
            toasts.error(t('teams.adminPage.courseEditPage.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <ModalRoot>
                <ModalInner>
                    <ModalTitle>{t(`${te}.modal.editTitle`)}</ModalTitle>
                </ModalInner>
            </ModalRoot>
        );
    }

    return (
        <ModalRoot>
            <ModalInner>
                <BackButton type="button" onClick={close}>
                    ← {t(`${te}.modal.back`)}
                </BackButton>
                <ModalTitle>
                    {campaignId ? t(`${te}.modal.editTitle`) : t(`${te}.modal.createTitle`)}
                </ModalTitle>

                <SectionBlock>
                    <SectionHeading>{t(`${te}.modal.generalInfo`)}</SectionHeading>
                    <TextInputField
                        label={t(`${te}.modal.name`)}
                        required
                        value={name}
                        maxLength={100}
                        placeholder={t(`${te}.modal.namePlaceholder`)}
                        onChange={(e) => setName(e.target.value.slice(0, 100))}
                    />
                    <SelectField
                        label={t(`${te}.modal.module`)}
                        required
                        options={moduleOptions}
                        value={selectedModuleOpts[selectedModuleOpts.length - 1] || null}
                        placeholder={t(`${te}.modal.modulePlaceholder`)}
                        onChange={(opt) => {
                            if (!opt?.value) return;
                            const nextId = String(opt.value);
                            setTeachersByGroup({});
                            setModuleIds((prev) =>
                                prev.includes(nextId)
                                    ? prev.filter((id) => id !== nextId)
                                    : [...prev, nextId],
                            );
                        }}
                    />
                    {selectedModuleOpts.length > 0 && (
                        <ChipList>
                            {selectedModuleOpts.map((item) => (
                                <TeacherChip key={item.value}>
                                    {item.label}
                                    <ChipRemove
                                        type="button"
                                        onClick={() => {
                                            setTeachersByGroup({});
                                            setModuleIds((prev) => prev.filter((id) => id !== item.value));
                                        }}
                                    >
                                        ×
                                    </ChipRemove>
                                </TeacherChip>
                            ))}
                        </ChipList>
                    )}
                </SectionBlock>

                <SectionBlock>
                    <SectionHeading>{t(`${te}.modal.assessmentMode`)}</SectionHeading>
                    <SelectField
                        label={t(`${te}.modal.format`)}
                        options={formatOptions}
                        value={formatOptions.find((o) => o.value === format) || null}
                        onChange={(opt) => {
                            if (opt?.value) setFormat(opt.value as TeachingEvaluationFormat);
                        }}
                    />
                    <div>
                        {votingLocked ? (
                            <VotingEmptyHint>{t(`${te}.modal.votingDisabledNoGroups`)}</VotingEmptyHint>
                        ) : null}
                        <VotingBlock $disabled={votingLocked}>
                            <VotingLabel>{t(`${te}.modal.voting`)}</VotingLabel>
                            <RadioList>
                                {votingModes.map((mode) => (
                                    <CheckLongreadRadioButton
                                        key={mode}
                                        name="votingMode"
                                        title={t(`${te}.voting.${mode}`)}
                                        checked={votingMode === mode}
                                        disabled={votingLocked}
                                        onChange={() => {
                                            if (!votingLocked) setVotingMode(mode);
                                        }}
                                        hintText={t(
                                            `${te}.modal.votingHints.${getVotingHintKey(format, mode)}`,
                                        )}
                                        hintPlace="right"
                                    />
                                ))}
                            </RadioList>
                        </VotingBlock>
                    </div>

                    {needsGroups && (
                        <>
                            <SelectField
                                label={t(`${te}.modal.groups`)}
                                required
                                options={groupOptions}
                                value={selectedGroupOpts[selectedGroupOpts.length - 1] || null}
                                placeholder={t(`${te}.modal.groupsPlaceholder`)}
                                onChange={(opt) => {
                                    if (!opt?.value) return;
                                    const nextId = String(opt.value);
                                    if (groupIds.includes(nextId)) {
                                        removeGroup(nextId);
                                        return;
                                    }
                                    setGroupIds((prev) => [...prev, nextId]);
                                }}
                            />
                            {selectedGroupOpts.length > 0 && (
                                <ChipList>
                                    {selectedGroupOpts.map((item) => (
                                        <TeacherChip key={item.value}>
                                            {item.label}
                                            <ChipRemove
                                                type="button"
                                                onClick={() => removeGroup(item.value)}
                                            >
                                                ×
                                            </ChipRemove>
                                        </TeacherChip>
                                    ))}
                                </ChipList>
                            )}
                        </>
                    )}

                    {needsTeachers &&
                        selectedGroupOpts.map((group) => (
                            <ProjectAuthorsWrapper
                                key={group.value}
                                isTeamEdit
                                direction="column"
                                directionMobile="column"
                            >
                                <ProjectAuthorsEdit
                                    label={t(`${te}.modal.teachersForGroup`, { group: group.label })}
                                    required
                                    value={teachersByGroup[group.value] || []}
                                    orientation="column"
                                    onChange={({ authors }) => setGroupTeachers(group.value, authors)}
                                    placeholder={t(`${te}.modal.teachersSearch`)}
                                    isTeamEdit
                                    isAdminPage
                                    teamId={teamId}
                                    localOptions={moduleTeachers}
                                    onSelect={(id) =>
                                        (teachersByGroup[group.value] || []).some(
                                            (teacher) =>
                                                teacher.accountId === id || teacher.designerId === id,
                                        )
                                    }
                                />
                            </ProjectAuthorsWrapper>
                        ))}
                </SectionBlock>

                <SectionBlock>
                    <SectionHeading>{t(`${te}.modal.dates`)}</SectionHeading>
                    <DatePickerField
                        label={t(`${te}.modal.openDate`)}
                        required
                        value={openAt}
                        onChange={setOpenAt}
                        locale={i18n.language}
                    />
                    <DatePickerField
                        label={t(`${te}.modal.closeDate`)}
                        required
                        value={closeAt}
                        onChange={setCloseAt}
                        locale={i18n.language}
                    />
                </SectionBlock>
            </ModalInner>
            <FooterBar>
                <FooterCancel type="button" disabled={isSaving} onClick={close}>
                    {t(`${te}.modal.cancel`)}
                </FooterCancel>
                <FooterActions>
                    <BrgButton
                        variant={ButtonVariant.Hollow}
                        disabled={isSaving || !canSaveDraft}
                        onClick={() => handleSave(true)}
                    >
                        {t(`${te}.modal.saveDraft`)}
                    </BrgButton>
                    <BrgButton
                        variant={ButtonVariant.Filled}
                        disabled={isSaving || !canPublish}
                        onClick={() => handleSave(false)}
                    >
                        {t(`${te}.modal.publish`)}
                    </BrgButton>
                </FooterActions>
            </FooterBar>
        </ModalRoot>
    );
};

export default TeachingEvaluationCampaignModal;
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API, Designer, DesignerAPI } from '@brg/api';
import { BrgButton, ButtonVariant, useToasts } from '@brg/ui';
import { useModal } from 'core';
import { TextInputField } from 'shared/pages/CourseEdit/components/TextInputField';
import { SelectField } from 'shared/pages/CourseEdit/components/SelectField';
import { DatePickerField } from 'shared/pages/CourseEdit/components/DatePickerField';
import CheckLongreadRadioButton from 'shared/pages/teams/components/CheckLongreadRadioButton';
import { useGetModulesList } from 'shared/hooks/api/LMS/useGetModulesList';
import { useGetGroupsInfoByCourseId } from 'shared/hooks/api/LMS/useGetGroupsInfoByCourseId';
import { useGetLMSModule } from 'shared/hooks/api/useGetLMSModule';
import { teachingEvaluationApi } from 'shared/pages/CourseEdit/teachingEvaluation/api';
import { resolveStatusFromDates } from 'shared/pages/CourseEdit/teachingEvaluation/mockStore';
import {
    TeachingEvaluationFormat,
    TeachingEvaluationVotingMode,
} from 'shared/pages/CourseEdit/teachingEvaluation/types';
import { toDisplayString } from 'shared/pages/CourseEdit/teachingEvaluation/toDisplayString';
import ProjectAuthorsEdit from 'shared/pages/projectEdit/ProjectAuthorsEdit';
import { ProjectAuthorsWrapper } from 'shared/pages/projectEdit/styles';
import { AuthorT } from 'shared/pages/projectEdit/types';
import {
    BackButton,
    ChipList,
    ChipRemove,
    FooterActions,
    FooterBar,
    FooterCancel,
    ModalInner,
    ModalRoot,
    ModalTitle,
    RadioList,
    SectionBlock,
    SectionHeading,
    TeacherChip,
    VotingBlock,
    VotingEmptyHint,
    VotingLabel,
} from './styles';

interface SelectOption {
    label: string;
    value: string;
}

const toStartOfLocalDayIso = (date: Date): string => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next.toISOString();
};

const toEndOfLocalDayIso = (date: Date): string => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next.toISOString();
};

const designerKey = (designer: Pick<Designer<'Local'>, 'designerId' | 'accountId'>): string =>
    String(designer.designerId || designer.accountId || '');

const designerDisplayName = (designer: Designer<'Local'>, lang: string): string =>
    [toDisplayString(designer.name, lang), toDisplayString(designer.surName, lang), toDisplayString(designer.middleName, lang)]
        .filter(Boolean)
        .join(' ')
        .trim() || designerKey(designer);

const stubDesigner = (id: string, name: string): AuthorT =>
    ({
        designerId: id,
        accountId: id,
        name,
        surName: '',
        middleName: '',
        nameCache: name,
        about: '',
        socialNetworks: [],
        workPlaces: [],
        education: [],
    }) as AuthorT;

const moduleTeacherToLocal = (p: any, lang: string): AuthorT => {
    const name = toDisplayString(p?.name, lang) || toDisplayString(p?.firstName, lang);
    const surName = toDisplayString(p?.surName, lang) || toDisplayString(p?.lastName, lang);
    const middleName = toDisplayString(p?.middleName, lang);
    const fullName = toDisplayString(p?.fullName, lang) || toDisplayString(p?.nameCache, lang);
    const id = String(p?.designerId || p?.accountId || p?.id || '');
    return {
        ...p,
        designerId: String(p?.designerId || id),
        accountId: String(p?.accountId || id),
        name: name || fullName,
        surName,
        middleName,
        nameCache: fullName || [name, surName, middleName].filter(Boolean).join(' '),
        about: toDisplayString(p?.about, lang),
        socialNetworks: Array.isArray(p?.socialNetworks) ? p.socialNetworks : [],
        workPlaces: Array.isArray(p?.workPlaces) ? p.workPlaces : [],
        education: Array.isArray(p?.education) ? p.education : [],
    } as AuthorT;
};

const mergeTeachersById = (list: AuthorT[]): AuthorT[] => {
    const unique = new Map<string, AuthorT>();
    list.forEach((teacher) => {
        const key = designerKey(teacher);
        if (key && !unique.has(key)) unique.set(key, teacher);
    });
    return Array.from(unique.values());
};

const designersFromGetList = (raw: unknown, lang: string): AuthorT[] => {
    const list = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { data?: unknown })?.data)
          ? ((raw as { data: unknown[] }).data)
          : [];
    return (API.toLang(list, lang) || []) as AuthorT[];
};

interface TeachingEvaluationCampaignModalProps {
    teamId: string;
    campaignId?: string;
    onSaved?: () => void;
}

const SOP_MODULE_STATUSES = [11];

const FORMAT_OPTIONS: TeachingEvaluationFormat[] = ['module', 'teacher', 'moduleAndTeacher'];

const getVotingModes = (format: TeachingEvaluationFormat): TeachingEvaluationVotingMode[] => {
    if (format === 'module') return ['general', 'custom'];
    return ['general', 'byGroups', 'custom'];
};

const getVotingHintKey = (format: TeachingEvaluationFormat, mode: TeachingEvaluationVotingMode) => {
    if (format === 'module') {
        if (mode === 'custom') return 'moduleCustom';
        return 'moduleGeneral';
    }
    if (format === 'teacher') {
        if (mode === 'byGroups') return 'teacherByGroups';
        if (mode === 'custom') return 'teacherCustom';
        return 'teacherGeneral';
    }
    if (mode === 'byGroups') return 'moduleAndTeacherByGroups';
    if (mode === 'custom') return 'moduleAndTeacherCustom';
    return 'moduleAndTeacherGeneral';
};

export const TeachingEvaluationCampaignModal: FC<TeachingEvaluationCampaignModalProps> = ({
    teamId,
    campaignId,
    onSaved,
}) => {
    const { t, i18n } = useTranslation();
    const modal = useModal();
    const toasts = useToasts();
    const te = 'teams.adminPage.courseEditPage.teachingEvaluation';
    const { modules } = useGetModulesList(teamId, null, SOP_MODULE_STATUSES);

    const [name, setName] = useState('');
    const [moduleIds, setModuleIds] = useState<string[]>([]);
    const [format, setFormat] = useState<TeachingEvaluationFormat>('moduleAndTeacher');
    const [votingMode, setVotingMode] = useState<TeachingEvaluationVotingMode>('general');
    const [groupIds, setGroupIds] = useState<string[]>([]);
    const [teachersByGroup, setTeachersByGroup] = useState<Record<string, AuthorT[]>>({});
    const [openAt, setOpenAt] = useState<Date | null>(null);
    const [closeAt, setCloseAt] = useState<Date | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(!!campaignId);
    const [moduleTeachers, setModuleTeachers] = useState<AuthorT[]>([]);

    const selectedModuleId = moduleIds[0] || '';
    const { LMSModuleInfo, isLoading: isModuleLoading } = useGetLMSModule(
        selectedModuleId,
        undefined,
        !selectedModuleId,
        false,
    );

    const moduleOptions = useMemo<SelectOption[]>(() => {
        const list = Array.isArray(modules) ? modules : [];
        return list
            .map((m: any, idx: number) => {
                const label =
                    toDisplayString(m?.title, i18n.language) ||
                    toDisplayString(m?.name, i18n.language) ||
                    t('course_edit.module_default_name', { index: idx + 1 });
                return {
                    label,
                    value: String(m?.projectId || m?.moduleId || m?.id || ''),
                };
            })
            .filter((o) => o.value);
    }, [modules, t, i18n.language]);

    const formatOptions = useMemo(
        () =>
            FORMAT_OPTIONS.map((value) => ({
                label: t(`${te}.format.${value}`),
                value,
            })),
        [t],
    );

    const votingModes = useMemo(() => getVotingModes(format), [format]);
    const { groups: courseGroups, isLoading: groupsLoading } = useGetGroupsInfoByCourseId(
        teamId,
        i18n.language,
    );

    const groupOptions = useMemo<SelectOption[]>(
        () =>
            courseGroups
                .map((group) => ({
                    label:
                        toDisplayString(group.name, i18n.language) ||
                        toDisplayString(group.title, i18n.language) ||
                        String(group.id || ''),
                    value: String(group.id || ''),
                }))
                .filter((option) => option.value),
        [courseGroups, i18n.language],
    );
    const votingLocked = !groupsLoading && groupOptions.length === 0;

    useEffect(() => {
        if (!votingModes.includes(votingMode)) {
            setVotingMode(votingModes[0]);
        }
    }, [votingModes, votingMode]);

    useEffect(() => {
        if (votingLocked && votingMode !== 'general') {
            setVotingMode('general');
        }
    }, [votingLocked, votingMode]);

    useEffect(() => {
        if (!selectedModuleId || isModuleLoading) {
            setModuleTeachers([]);
            return;
        }
        if (!LMSModuleInfo) {
            setModuleTeachers([]);
            return;
        }

        const fromObjects = mergeTeachersById(
            (Array.isArray(LMSModuleInfo.teachers) ? LMSModuleInfo.teachers : []).map((p) =>
                moduleTeacherToLocal(p, i18n.language),
            ),
        );
        const knownIds = new Set(
            fromObjects.flatMap((teacher) => [teacher.designerId, teacher.accountId].filter(Boolean)),
        );
        const missingIds = (LMSModuleInfo.teacherIds || []).map(String).filter((id) => id && !knownIds.has(id));

        if (!missingIds.length) {
            setModuleTeachers(fromObjects);
            return;
        }

        let cancelled = false;
        DesignerAPI.GetList({ designerIds: missingIds, limit: missingIds.length })
            .then((r) => r.json())
            .then((raw) => {
                if (cancelled) return;
                const fetched = designersFromGetList(raw, i18n.language).map((p) =>
                    moduleTeacherToLocal(p, i18n.language),
                );
                setModuleTeachers(mergeTeachersById([...fromObjects, ...fetched]));
            })
            .catch(() => {
                if (!cancelled) setModuleTeachers(fromObjects);
            });

        return () => {
            cancelled = true;
        };
    }, [LMSModuleInfo, isModuleLoading, selectedModuleId, i18n.language]);

    useEffect(() => {
        if (!moduleTeachers.length) return;
        setTeachersByGroup((prev) => {
            let changed = false;
            const next: Record<string, AuthorT[]> = {};
            Object.keys(prev).forEach((groupId) => {
                next[groupId] = (prev[groupId] || []).map((sel) => {
                    const keys = [sel.designerId, sel.accountId].filter(Boolean);
                    const fromModule = moduleTeachers.find(
                        (teacher) =>
                            keys.includes(teacher.designerId) || keys.includes(teacher.accountId),
                    );
                    if (fromModule && fromModule !== sel) {
                        changed = true;
                        return fromModule;
                    }
                    return sel;
                });
            });
            return changed ? next : prev;
        });
    }, [moduleTeachers]);

    useEffect(() => {
        if (!campaignId) return;
        let cancelled = false;
        setIsLoading(true);
        teachingEvaluationApi
            .get(campaignId)
            .then(async (campaign) => {
                if (!campaign || cancelled) return;
                setName((campaign.name || '').slice(0, 100));
                setModuleIds(
                    campaign.moduleIds?.length
                        ? campaign.moduleIds
                        : campaign.moduleId
                          ? [campaign.moduleId]
                          : [],
                );
                setFormat(campaign.format);
                setVotingMode(campaign.votingMode);
                setGroupIds(campaign.groupIds || []);
                setOpenAt(campaign.openAt ? new Date(campaign.openAt) : null);
                setCloseAt(campaign.closeAt ? new Date(campaign.closeAt) : null);

                const custom = campaign.customVoteGroups || [];
                const ids = Array.from(
                    new Set(custom.flatMap((group) => group.teacherIds).filter(Boolean)),
                );
                if (!ids.length) {
                    setTeachersByGroup({});
                    return;
                }
                const nameById = new Map<string, string>();
                (campaign.teacherIds || []).forEach((id, idx) => {
                    const name = campaign.teacherNames?.[idx];
                    if (id && name) nameById.set(id, name);
                });
                const byId = new Map<string, AuthorT>();
                try {
                    const raw = await DesignerAPI.GetList({ designerIds: ids, limit: ids.length }).then((r) =>
                        r.json(),
                    );
                    if (cancelled) return;
                    designersFromGetList(raw, i18n.language).forEach((designer) => {
                        if (designer.designerId) byId.set(designer.designerId, designer);
                        if (designer.accountId) byId.set(designer.accountId, designer);
                    });
                } catch {
                }
                if (cancelled) return;
                const resolveTeacher = (id: string) =>
                    byId.get(id) || stubDesigner(id, nameById.get(id) || id);
                setTeachersByGroup(
                    custom.reduce<Record<string, AuthorT[]>>((acc, group) => {
                        acc[group.groupId] = group.teacherIds.filter(Boolean).map(resolveTeacher);
                        return acc;
                    }, {}),
                );
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [campaignId, i18n.language]);

    const needsGroups = votingMode === 'custom';
    const needsTeachers =
        votingMode === 'custom' && (format === 'teacher' || format === 'moduleAndTeacher');
    const hasName = Boolean(name.trim());
    const hasModule = moduleIds.length > 0;
    const hasDates = Boolean(openAt && closeAt);
    const datesOrderOk =
        !openAt ||
        !closeAt ||
        new Date(closeAt).setHours(0, 0, 0, 0) >= new Date(openAt).setHours(0, 0, 0, 0);
    const hasGroups = !needsGroups || groupIds.length > 0;
    const hasTeachers =
        !needsTeachers ||
        (groupIds.length > 0 &&
            groupIds.every((id) => (teachersByGroup[id] || []).length > 0));
    const canSaveDraft = hasName && hasModule && hasDates && datesOrderOk;
    const canPublish = canSaveDraft && hasGroups && hasTeachers;

    const selectedModuleOpts = useMemo(
        () =>
            moduleIds.map(
                (id) => moduleOptions.find((option) => option.value === id) || { label: id, value: id },
            ),
        [moduleIds, moduleOptions],
    );

    const selectedGroupOpts = useMemo(
        () =>
            groupIds.map(
                (id) => groupOptions.find((g) => g.value === id) || { label: id, value: id },
            ),
        [groupIds, groupOptions],
    );

    const close = useCallback(() => modal.hide(), [modal]);

    const setGroupTeachers = useCallback((groupId: string, authors: AuthorT[]) => {
        setTeachersByGroup((prev) => ({ ...prev, [groupId]: authors }));
    }, []);

    const removeGroup = useCallback((groupId: string) => {
        setGroupIds((prev) => prev.filter((id) => id !== groupId));
        setTeachersByGroup((prev) => {
            if (!(groupId in prev)) return prev;
            const next = { ...prev };
            delete next[groupId];
            return next;
        });
    }, []);

    const buildPayload = (asDraft: boolean) => {
        const openIso = openAt ? toStartOfLocalDayIso(openAt) : '';
        const closeIso = closeAt ? toEndOfLocalDayIso(closeAt) : '';
        let status: 'draft' | 'scheduled' | 'active' | 'completed' = 'draft';
        if (!asDraft) {
            status =
                openIso && closeIso
                    ? resolveStatusFromDates(openIso, closeIso, 'active')
                    : 'active';
        }
        const customVoteGroups = needsGroups
            ? groupIds.map((groupId) => ({
                  groupId,
                  teacherIds: needsTeachers
                      ? (teachersByGroup[groupId] || []).map(designerKey).filter(Boolean)
                      : [],
              }))
            : [];
        const allTeachers = needsTeachers
            ? mergeTeachersById(groupIds.flatMap((groupId) => teachersByGroup[groupId] || []))
            : [];
        return {
            teamId,
            name: name.trim(),
            moduleId: selectedModuleOpts[0]?.value || '',
            moduleName: selectedModuleOpts[0]?.label || '',
            moduleIds: selectedModuleOpts.map((option) => option.value),
            format,
            votingMode,
            groupIds: needsGroups ? groupIds : [],
            customVoteGroups,
            teacherIds: allTeachers.map(designerKey).filter(Boolean),
            teacherNames: allTeachers.map((item) => designerDisplayName(item, i18n.language)),
            openAt: openIso,
            closeAt: closeIso,
            status,
        };
    };

    const handleSave = async (asDraft: boolean) => {
        if (asDraft) {
            if (!hasModule) {
                toasts.info(t(`${te}.modal.selectModule`));
                return;
            }
            if (!canSaveDraft) {
                toasts.info(t(`${te}.modal.fillRequired`));
                return;
            }
        } else {
            if (!datesOrderOk) {
                toasts.info(t(`${te}.modal.datesOrder`));
                return;
            }
            if (!canPublish) {
                toasts.info(t(`${te}.modal.fillRequired`));
                return;
            }
        }
        setIsSaving(true);
        try {
            const payload = buildPayload(asDraft);
            if (campaignId) {
                await teachingEvaluationApi.update(campaignId, payload);
            } else {
                await teachingEvaluationApi.create(payload);
            }
            toasts.info(t(asDraft ? `${te}.saved` : `${te}.published`));
            onSaved?.();
            close();
        } catch {
            toasts.error(t('teams.adminPage.courseEditPage.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <ModalRoot>
                <ModalInner>
                    <ModalTitle>{t(`${te}.modal.editTitle`)}</ModalTitle>
                </ModalInner>
            </ModalRoot>
        );
    }

    return (
        <ModalRoot>
            <ModalInner>
                <BackButton type="button" onClick={close}>
                    ← {t(`${te}.modal.back`)}
                </BackButton>
                <ModalTitle>
                    {campaignId ? t(`${te}.modal.editTitle`) : t(`${te}.modal.createTitle`)}
                </ModalTitle>

                <SectionBlock>
                    <SectionHeading>{t(`${te}.modal.generalInfo`)}</SectionHeading>
                    <TextInputField
                        label={t(`${te}.modal.name`)}
                        required
                        value={name}
                        maxLength={100}
                        placeholder={t(`${te}.modal.namePlaceholder`)}
                        onChange={(e) => setName(e.target.value.slice(0, 100))}
                    />
                    <SelectField
                        label={t(`${te}.modal.module`)}
                        required
                        options={moduleOptions}
                        value={selectedModuleOpts[selectedModuleOpts.length - 1] || null}
                        placeholder={t(`${te}.modal.modulePlaceholder`)}
                        onChange={(opt) => {
                            if (!opt?.value) return;
                            const nextId = String(opt.value);
                            setTeachersByGroup({});
                            setModuleIds((prev) =>
                                prev.includes(nextId)
                                    ? prev.filter((id) => id !== nextId)
                                    : [...prev, nextId],
                            );
                        }}
                    />
                    {selectedModuleOpts.length > 0 && (
                        <ChipList>
                            {selectedModuleOpts.map((item) => (
                                <TeacherChip key={item.value}>
                                    {item.label}
                                    <ChipRemove
                                        type="button"
                                        onClick={() => {
                                            setTeachersByGroup({});
                                            setModuleIds((prev) => prev.filter((id) => id !== item.value));
                                        }}
                                    >
                                        ×
                                    </ChipRemove>
                                </TeacherChip>
                            ))}
                        </ChipList>
                    )}
                </SectionBlock>

                <SectionBlock>
                    <SectionHeading>{t(`${te}.modal.assessmentMode`)}</SectionHeading>
                    <SelectField
                        label={t(`${te}.modal.format`)}
                        options={formatOptions}
                        value={formatOptions.find((o) => o.value === format) || null}
                        onChange={(opt) => {
                            if (opt?.value) setFormat(opt.value as TeachingEvaluationFormat);
                        }}
                    />
                    <div>
                        {votingLocked ? (
                            <VotingEmptyHint>{t(`${te}.modal.votingDisabledNoGroups`)}</VotingEmptyHint>
                        ) : null}
                        <VotingBlock $disabled={votingLocked}>
                            <VotingLabel>{t(`${te}.modal.voting`)}</VotingLabel>
                            <RadioList>
                                {votingModes.map((mode) => (
                                    <CheckLongreadRadioButton
                                        key={mode}
                                        name="votingMode"
                                        title={t(`${te}.voting.${mode}`)}
                                        checked={votingMode === mode}
                                        disabled={votingLocked}
                                        onChange={() => {
                                            if (!votingLocked) setVotingMode(mode);
                                        }}
                                        hintText={t(
                                            `${te}.modal.votingHints.${getVotingHintKey(format, mode)}`,
                                        )}
                                        hintPlace="right"
                                    />
                                ))}
                            </RadioList>
                        </VotingBlock>
                    </div>

                    {needsGroups && (
                        <>
                            <SelectField
                                label={t(`${te}.modal.groups`)}
                                required
                                options={groupOptions}
                                value={selectedGroupOpts[selectedGroupOpts.length - 1] || null}
                                placeholder={t(`${te}.modal.groupsPlaceholder`)}
                                onChange={(opt) => {
                                    if (!opt?.value) return;
                                    const nextId = String(opt.value);
                                    if (groupIds.includes(nextId)) {
                                        removeGroup(nextId);
                                        return;
                                    }
                                    setGroupIds((prev) => [...prev, nextId]);
                                }}
                            />
                            {selectedGroupOpts.length > 0 && (
                                <ChipList>
                                    {selectedGroupOpts.map((item) => (
                                        <TeacherChip key={item.value}>
                                            {item.label}
                                            <ChipRemove
                                                type="button"
                                                onClick={() => removeGroup(item.value)}
                                            >
                                                ×
                                            </ChipRemove>
                                        </TeacherChip>
                                    ))}
                                </ChipList>
                            )}
                        </>
                    )}

                    {needsTeachers &&
                        selectedGroupOpts.map((group) => (
                            <ProjectAuthorsWrapper
                                key={group.value}
                                isTeamEdit
                                direction="column"
                                directionMobile="column"
                            >
                                <ProjectAuthorsEdit
                                    label={t(`${te}.modal.teachersForGroup`, { group: group.label })}
                                    required
                                    value={teachersByGroup[group.value] || []}
                                    orientation="column"
                                    onChange={({ authors }) => setGroupTeachers(group.value, authors)}
                                    placeholder={t(`${te}.modal.teachersSearch`)}
                                    isTeamEdit
                                    isAdminPage
                                    teamId={teamId}
                                    localOptions={moduleTeachers}
                                    onSelect={(id) =>
                                        (teachersByGroup[group.value] || []).some(
                                            (teacher) =>
                                                teacher.accountId === id || teacher.designerId === id,
                                        )
                                    }
                                />
                            </ProjectAuthorsWrapper>
                        ))}
                </SectionBlock>

                <SectionBlock>
                    <SectionHeading>{t(`${te}.modal.dates`)}</SectionHeading>
                    <DatePickerField
                        label={t(`${te}.modal.openDate`)}
                        required
                        value={openAt}
                        onChange={setOpenAt}
                        locale={i18n.language}
                    />
                    <DatePickerField
                        label={t(`${te}.modal.closeDate`)}
                        required
                        value={closeAt}
                        onChange={setCloseAt}
                        locale={i18n.language}
                    />
                </SectionBlock>
            </ModalInner>
            <FooterBar>
                <FooterCancel type="button" disabled={isSaving} onClick={close}>
                    {t(`${te}.modal.cancel`)}
                </FooterCancel>
                <FooterActions>
                    <BrgButton
                        variant={ButtonVariant.Hollow}
                        disabled={isSaving || !canSaveDraft}
                        onClick={() => handleSave(true)}
                    >
                        {t(`${te}.modal.saveDraft`)}
                    </BrgButton>
                    <BrgButton
                        variant={ButtonVariant.Filled}
                        disabled={isSaving || !canPublish}
                        onClick={() => handleSave(false)}
                    >
                        {t(`${te}.modal.publish`)}
                    </BrgButton>
                </FooterActions>
            </FooterBar>
        </ModalRoot>
    );
};

export default TeachingEvaluationCampaignModal;
